import React, { useState, useEffect } from 'react';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';
import {
  getNextQuarterlyDeadline,
  PENALTY_EASEMENT,
  POSITIONING,
  QUARTERLY_DEADLINES_SOURCE,
} from '../../compliance/mtdFacts';

const PenaltyCalculator = () => {
  const [yearMode, setYearMode] = useState('2026'); // '2026' or '2027'
  const [lateUpdates, setLateUpdates] = useState(0);
  const nextDeadline = getNextQuarterlyDeadline();

  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0 });

  useEffect(() => {
    if (!nextDeadline) {
      setTimeLeft({ days: 0, hours: 0 });
      return undefined;
    }

    const targetDate = new Date(`${nextDeadline.deadline}T00:00:00Z`).getTime();

    const tick = () => {
      const difference = targetDate - Date.now();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        });
        return true;
      }
      setTimeLeft({ days: 0, hours: 0 });
      return false;
    };

    tick();
    const interval = setInterval(() => {
      if (!tick()) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [nextDeadline]);

  useEffect(() => {
    if (lateUpdates > 0) {
      const timer = setTimeout(() => {
        trackEvent('tool_completion', { tool_name: 'penalty_countdown' });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [lateUpdates, yearMode]);

  const hasFine = yearMode === '2027' && lateUpdates >= 4;
  const isSoftLanding = yearMode === '2026';

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to understand MTD quarterly-update penalties",
    "dateModified": getFreshnessDateIso(),
    "step": [
      { "@type": "HowToStep", "text": "Keep digital records and send quarterly updates through compatible software." },
      { "@type": "HowToStep", "text": PENALTY_EASEMENT.description },
      { "@type": "HowToStep", "text": POSITIONING.noSubmit }
    ]
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl my-12">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <FreshnessDate className="mb-4" />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="w-full">
          <h3 className="text-3xl font-extrabold text-white mb-4">MTD Penalty Countdown</h3>
          <AuthorCredit />

          <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
            <p className="text-xl font-medium leading-relaxed">
              <strong>{PENALTY_EASEMENT.description}</strong>
            </p>
          </div>

          <div className="prose prose-invert max-w-none text-slate-300 mb-6">
            <p>
              According to <strong>HMRC</strong> guidance, you still need to keep digital records and send quarterly updates in 2026/27 even though late updates do not attract penalty points that year.
              Capture receipts as you go so the records are ready for compatible filing software.
              {POSITIONING.noSubmit}
            </p>
          </div>
          <p className="text-slate-400 text-sm font-semibold">See how penalty points work after the 2026/27 easement below.</p>
        </div>
        {nextDeadline ? (
          <div className="bg-indigo-900/40 border border-indigo-500/30 px-4 py-2 rounded-xl text-center shadow-inner">
            <p className="text-[10px] text-indigo-300 uppercase tracking-widest font-semibold mb-1">Next deadline: {nextDeadline.label}</p>
            <div className="flex items-center justify-center gap-2 font-mono text-white text-lg font-bold">
              <span>{timeLeft.days}d</span>
              <span>:</span>
              <span>{timeLeft.hours}h</span>
            </div>
          </div>
        ) : (
          <div className="bg-indigo-900/40 border border-indigo-500/30 px-4 py-2 rounded-xl text-center shadow-inner">
            <p className="text-[10px] text-indigo-300 uppercase tracking-widest font-semibold mb-1">2026/27 quarterly dates</p>
            <p className="text-white text-sm font-semibold">See current HMRC deadlines</p>
          </div>
        )}
      </div>

      <div className="mb-8">
        <label className="block text-sm font-semibold text-slate-300 mb-3">Select Tax Year:</label>
        <div className="flex bg-black/40 p-1 rounded-xl border border-white/10 w-fit">
          <button
            onClick={() => setYearMode('2026')}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${yearMode === '2026' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >
            2026/27 (Current)
          </button>
          <button
            onClick={() => setYearMode('2027')}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${yearMode === '2027' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
          >
            2027/28 (Next Year)
          </button>
        </div>
      </div>

      <div className="mb-10">
        <div className="flex justify-between items-end mb-4">
          <label className="block text-sm font-semibold text-slate-300">Number of Late Quarterly Updates:</label>
          <span className="text-2xl font-mono font-bold text-white bg-black/30 px-3 py-1 rounded-lg border border-white/5">{lateUpdates}</span>
        </div>
        <input
          type="range"
          min="0"
          max="8"
          value={lateUpdates}
          onChange={(e) => setLateUpdates(parseInt(e.target.value))}
          className="w-full accent-indigo-500 h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
          <span>0 (On time)</span>
          <span>4 (Threshold)</span>
          <span>8</span>
        </div>
      </div>

      <div className={`p-6 rounded-2xl border transition-all duration-500 flex flex-col items-center text-center shadow-2xl ${
        isSoftLanding
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : hasFine
            ? 'bg-red-500/10 border-red-500/30'
            : 'bg-amber-500/10 border-amber-500/30'
      }`}>
        <h4 className="text-xl font-bold text-white mb-2">Penalty Status</h4>

        {isSoftLanding ? (
          <>
            <span className="inline-block px-4 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-bold shadow-lg shadow-emerald-500/20 mb-3 uppercase tracking-wider">
              Soft Landing Protection
            </span>
            <p className="text-slate-300 text-sm max-w-md">{PENALTY_EASEMENT.yearOne} You still need to send the updates. Illustrated fine: £0.</p>
          </>
        ) : hasFine ? (
          <>
            <span className="inline-block px-5 py-2 bg-red-500 text-white rounded-full text-lg font-black shadow-lg shadow-red-500/30 mb-3 tracking-widest uppercase">
              £200 PENALTY
            </span>
            <p className="text-slate-300 text-sm max-w-md">{PENALTY_EASEMENT.laterYears}</p>
          </>
        ) : (
          <>
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4].map((pt) => (
                <div key={pt} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  lateUpdates >= pt ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/30' : 'bg-black/30 text-slate-600 border border-white/10'
                }`}>
                  {pt}
                </div>
              ))}
            </div>
            <p className="text-slate-300 text-sm max-w-md">{PENALTY_EASEMENT.laterYears}</p>
          </>
        )}
      </div>


      <div className="mt-6 mb-2 text-center">
        <a href={QUARTERLY_DEADLINES_SOURCE.source} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
          {QUARTERLY_DEADLINES_SOURCE.sourceAlias} →
        </a>
      </div>


    </div>
  );
};

export default PenaltyCalculator;
